import { XMarkIcon } from '@heroicons/react/24/solid'
import cx from 'classnames'
import { type FC, type MouseEventHandler } from 'react'
import { twMerge } from 'tailwind-merge'
import { Button, ButtonVariant } from '../button/button'
import { type DefaultProps } from '../common'
import { AlertErrorIcon, AlertWarningIcon, AlertInfoIcon, AlertSuccessIcon } from '../icons/alert'

export enum AlertType {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
}

export enum AlertBgVariant {
  Filled = 'filled',
  Outlined = 'outlined',
}

export interface AlertProps extends DefaultProps {
  /**
   * Type
   */
  alertType: AlertType

  /**
   * Background Type
   */
  bgVariant?: AlertBgVariant // default is filled

  showIcon?: boolean // default is true

  onClose?: () => void // If provided, the alert will have a close button

  title?: string

  description?: string

  /**
   * CTA button text - if provided, the alert will have a CTA button
   */
  ctaLabel?: string

  /**
   * CTA click handler - you should set ctaLabel property, otherwise will do nothing
   */
  ctaCallback?: MouseEventHandler<HTMLButtonElement>
}

type AlertIconProps = DefaultProps & {
  width?: number
  height?: number
}

export const Alert: FC<AlertProps> = ({
  alertType,
  bgVariant = AlertBgVariant.Filled,
  showIcon = true,
  onClose,
  title,
  description,
  ctaLabel,
  ctaCallback,
  className,
  id,
  testId,
  ariaLabel,
  children,
}): JSX.Element => {
  // Base container classes
  const baseClass = cx('flex items-start gap-xs p-sm rounded-lg', {
    // Filled background variants
    'bg-sky-50 bg-opacity-50 border border-sky-50': bgVariant === AlertBgVariant.Filled && alertType === AlertType.Info,
    'bg-green-50 bg-opacity-50 border border-green-50':
      bgVariant === AlertBgVariant.Filled && alertType === AlertType.Success,
    'bg-orange-50 bg-opacity-50 border border-orange-50':
      bgVariant === AlertBgVariant.Filled && alertType === AlertType.Warning,
    'bg-red-50 bg-opacity-50 border border-red-50':
      bgVariant === AlertBgVariant.Filled && alertType === AlertType.Error,
    // Outlined variants
    'border border-sky-100 bg-white': bgVariant === AlertBgVariant.Outlined && alertType === AlertType.Info,
    'border border-green-100 bg-white': bgVariant === AlertBgVariant.Outlined && alertType === AlertType.Success,
    'border border-orange-100 bg-white': bgVariant === AlertBgVariant.Outlined && alertType === AlertType.Warning,
    'border border-red-100 bg-white': bgVariant === AlertBgVariant.Outlined && alertType === AlertType.Error,
  })

  // Icon container classes - round background with proper color
  const iconContainerClass = cx('flex items-center justify-center w-xl h-xl rounded-full flex-shrink-0', {
    'bg-sky-100': alertType === AlertType.Info,
    'bg-green-200': alertType === AlertType.Success,
    'bg-orange-200': alertType === AlertType.Warning,
    'bg-red-200': alertType === AlertType.Error,
  })

  // Icon color classes
  const iconClass = 'w-fit h-fit'

  // Title classes
  const titleClass = cx('typo-primary-base-semibold', {
    'text-sky-600': alertType === AlertType.Info,
    'text-green-700': alertType === AlertType.Success,
    'text-orange-700': alertType === AlertType.Warning,
    'text-red-700': alertType === AlertType.Error,
  })

  // Description classes
  const descriptionClass = cx('typo-primary-base-regular', {
    'text-sky-600': alertType === AlertType.Info,
    'text-green-600': alertType === AlertType.Success,
    'text-orange-600': alertType === AlertType.Warning,
    'text-red-600': alertType === AlertType.Error,
  })

  // Close button classes
  const closeButtonClass = cx(
    'flex items-center justify-center w-[1.25rem] h-[1.25rem] flex-shrink-0 cursor-pointer hover:opacity-70 transition-opacity',
    {
      'text-sky-600': alertType === AlertType.Info,
      'text-green-500': alertType === AlertType.Success,
      'text-orange-600': alertType === AlertType.Warning,
      'text-red-600': alertType === AlertType.Error,
    },
  )

  // CTA button classes
  const ctaButtonClass = cx('mt-2xs flex items-center gap-3xs typo-primary-sm-semibold w-fit')

  const containerClasses = twMerge(baseClass, className)

  // Render icon based on alert type
  const renderIcon = (): JSX.Element | null => {
    if (!showIcon) return null

    let IconComponent: FC<AlertIconProps>

    switch (alertType) {
      case AlertType.Info:
        IconComponent = AlertInfoIcon
        break
      case AlertType.Success:
        IconComponent = AlertSuccessIcon
        break
      case AlertType.Warning:
        IconComponent = AlertWarningIcon
        break
      case AlertType.Error:
        IconComponent = AlertErrorIcon
        break
      default:
        IconComponent = AlertInfoIcon
    }

    return (
      <div className={iconContainerClass}>
        <IconComponent className={iconClass} />
      </div>
    )
  }

  // Determine if we should center the text content to the icon
  const hasOnlyTitleOrDescription = Boolean((title && !description) || (!title && description))

  return (
    <div
      className={containerClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      {renderIcon()}

      <div
        className={cx('flex-1 flex flex-col', {
          'self-center': hasOnlyTitleOrDescription,
        })}
      >
        {title ? <p className={titleClass}>{title}</p> : null}
        {description ? <p className={descriptionClass}>{description}</p> : null}
        {ctaLabel && ctaCallback ? (
          <Button size="small" variant={ButtonVariant.Secondary} onClick={ctaCallback} className={ctaButtonClass}>
            {ctaLabel}
          </Button>
        ) : null}
        {children}
      </div>

      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          className={closeButtonClass}
          aria-label="Close alert"
          data-testid={testId ? `${testId}-close` : undefined}
        >
          <XMarkIcon className="w-lg h-lg" />
        </button>
      ) : null}
    </div>
  )
}
